import * as React from 'react';
import { useState } from 'react';
import { getUnits } from '@/api/units';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { Building as BuildingIcon, Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { Unit } from '@/types/v2/unit';
import { useUser } from '@/hooks/v2/use-user';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { Seo } from '@/components/seo';

import { UnitsTable } from '@/pages/v2/units/components/units-table';
import { CreateOrEditUnitModal } from './components/create-or-edit-unit-modal';

export function UnitsPage(): React.JSX.Element {
  const { community, isLoading: isLoadingUser } = useUser();
  const [currentPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`${community?.id}-units`, currentPage],
    queryFn: () => getUnits({ page: currentPage, per_page: 200 }),
  });

  const units = data?.units ?? [];

  const handleOnSuccess = React.useCallback((_unit: Unit) => {
    toast.success('Unit created successfully');
    setCreateOpen(false);
    refetch();
  }, [refetch]);

  return (
    <>
      <Seo title="Units" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Units</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                  Manage units, residents, and property details
                </Typography>
              </Box>
              <RoleGuard
                requiredAccountRoles={[
                  ACCOUNT_ROLES.ACCOUNT_OWNER,
                  ACCOUNT_ROLES.ACCOUNT_ADMIN,
                  ACCOUNT_ROLES.ACCOUNT_MANAGER,
                ]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
              >
                <Button
                  onClick={() => setCreateOpen(true)}
                  size="small"
                  startIcon={<PlusIcon size={16} />}
                  variant="contained"
                >
                  Add Unit
                </Button>
              </RoleGuard>
            </Stack>
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            <UnitsTable
              loading={isLoading || isLoadingUser}
              onBulkDeleteSuccess={() => refetch()}
              onDeleteSuccess={() => refetch()}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              onSuccess={handleOnSuccess}
              pagination={data?.meta}
              rows={units}
            />
          </Paper>
        </Container>
      </Box>

      <CreateOrEditUnitModal
        communityId={community?.id}
        communityUnitTypes={community?.unit_types}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSuccess={handleOnSuccess}
        open={createOpen}
      />
    </>
  );
}
